import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import { usersManagementServices } from '../APIServices/usersManagementServices';
import { studyManagementServices } from '../APIServices/studyManagementServices';
import { knowledgeLibraryServices } from '../APIServices/knowledgeLibraryServices';
import { reportServices } from '../APIServices/reportServices';
import { nid, nowISO } from '../utils/helpers';

export const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [state, setState] = useState({
    loaded: true,
    users: [],
    projects: [],
    studies: [],
    documents: [],
    knowledge: [],
    feasibilities: [],
    risks: [],
    tasks: [],
    milestones: [],
    audit: [],
    reports: [],
    currentUserId: '',
    isAuthenticated: false,
    user: null
  });
  const [currentUserData, setCurrentUserData] = useState(null);
  const [currentUserLoader, setCurrentUserLoader] = useState(false);

  // Check for existing session on load
  useEffect(() => {
    const savedSession = localStorage.getItem('arcolab_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          user: session.user
        }));
      } catch {
        localStorage.removeItem('arcolab_session');
      }
    }
  }, []);

  const getCurrentUserDetails = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCurrentUserData(null);
      return;
    }

    try {
      setCurrentUserLoader(true);
      const data = await usersManagementServices.getCurrentUserDetails();
      const user = data?.data || null;

      setCurrentUserData(user);
      setState((prev) => ({
        ...prev,
        currentUserId: user?.user || user?.userId || user?.id || prev.currentUserId,
      }));
    } catch (error) {
      setCurrentUserData(null);
    } finally {
      setCurrentUserLoader(false);
    }
  }, []);

  const loadUserList = useCallback(async () => {
    try {
      const [usersResponse, subAdminsResponse] = await Promise.all([
        usersManagementServices.getUserData('all', 'user', '', 1, 1000),
        usersManagementServices.getUserData('all', 'subadmin', '', 1, 1000),
      ]);

      const userRows = usersResponse?.data || [];
      const subAdminRows = subAdminsResponse?.data || [];

      const normalized = [...userRows, ...subAdminRows].map((row) => {
        const id = row?.user || row?.userId || row?.id || '';
        const fullName = [row?.firstName, row?.middleName, row?.lastName].filter(Boolean).join(' ').trim();
        const name = fullName || row?.name || row?.email || '';
        const initials = name
          ? name
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toUpperCase()
          : 'U';

        return {
          ...row,
          id,
          name,
          initials,
        };
      }).filter((row) => row.id);

      const uniqueUsers = Array.from(
        new Map(normalized.map((item) => [item.id, item])).values()
      );

      setState((prev) => ({
        ...prev,
        users: uniqueUsers,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        users: [],
      }));
    }
  }, []);

  const loadStudyList = useCallback(async () => {
    try {
      const data = await studyManagementServices.getStudyManagement('all', '', 1, 1000);
      const rows = data?.data || [];

      const mappedStudies = rows.map((item) => ({
        id: item.studyId || item.id,
        _id: item._id,
        studyId: item.studyId,
        studyCode: item.studyCode || item.code,
        code: item.studyCode || item.code,
        protocolNumber: item.protocolNumber || item.studyCode || item.code || '',
        studyLabel: item.studyLabel || '',
        name: item.studyLabel || item.studyCode || item.code || '',
        protocolTitle: item.protocolTitle || '',
        clientId: item.clientId || '',
        molecule: item.moleculeProduct || item.molecule || '',
        moleculeProduct: item.moleculeProduct || item.molecule || '',
        phase: item.phaseType || item.phase || '',
        phaseType: item.phaseType || item.phase || '',
        cro: item.cro || '',
        status: item.status || 'active',
        plannedStart: item.plannedStart,
        plannedEnd: item.plannedEnd,
      }));

      setState((prev) => ({
        ...prev,
        studies: mappedStudies,
        projects: mappedStudies,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        studies: [],
        projects: [],
      }));
    }
  }, []);

  const loadKnowledgeList = useCallback(async () => {
    try {
      const data = await knowledgeLibraryServices.getKnowledgeLibraryData('active');
      const rows = data?.data || [];

      const mapped = rows.map((item) => ({
        ...item,
        id: item.id || item.knowledgeId || item._id,
      }));

      setState((prev) => ({
        ...prev,
        knowledge: mapped,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        knowledge: [],
      }));
    }
  }, []);

  const loadReportList = useCallback(async () => {
    try {
      const data = await reportServices.getReportData('active');
      const rows = data?.data || [];

      const mapped = rows.map((item) => ({
        ...item,
        id: item.id || item.reportId || item._id,
      }));

      setState((prev) => ({
        ...prev,
        reports: mapped,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        reports: [],
      }));
    }
  }, []);

  useEffect(() => {
    getCurrentUserDetails();
    loadUserList();
    loadStudyList();
    loadKnowledgeList();
    loadReportList();
  }, [getCurrentUserDetails, loadUserList, loadStudyList, loadKnowledgeList, loadReportList]);

  const currentUser = useMemo(
    () => {
      const idFromCurrentUserData = currentUserData?.user || currentUserData?.userId || currentUserData?.id;
      const byId = state.users.find((u) => String(u.id) === String(idFromCurrentUserData || state.currentUserId));

      if (byId) {
        return byId;
      }

      if (currentUserData) {
        const fullName = [currentUserData?.firstName, currentUserData?.middleName, currentUserData?.lastName].filter(Boolean).join(' ').trim();
        return {
          ...currentUserData,
          id: idFromCurrentUserData,
          name: fullName || currentUserData?.name || currentUserData?.email || 'Current User',
          initials: (fullName || currentUserData?.name || 'U')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join('')
            .toUpperCase(),
        };
      }

      return state.users[0] || { id: '', name: 'User', initials: 'U' };
    },
    [state.users, state.currentUserId, currentUserData]
  );

  const audit = useCallback((action, object, detail = '', extra = {}) => {
    setState((prev) => ({
      ...prev,
      audit: [{ id: nid('a'), at: nowISO(), userId: currentUser?.id || prev.currentUserId || 'system', action, object, detail, ...extra }, ...prev.audit]
    }));
  }, [currentUser]);

  const handleLogin = useCallback((userData) => {
    setState(prev => ({
      ...prev,
      isAuthenticated: true,
      user: userData
    }));
    localStorage.setItem('arcolab_session', JSON.stringify({ user: userData }));
    getCurrentUserDetails();
    loadUserList();
    loadStudyList();
  }, [getCurrentUserDetails, loadUserList, loadStudyList]);

  const handleLogout = useCallback(() => {
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      user: null
    }));
    localStorage.removeItem('arcolab_session');
  }, []);

  const api = useMemo(() => ({
    state,
    setState,
    currentUser,
    currentUserData,
    currentUserLoader,
    getCurrentUserDetails,
    loadUserList,
    loadStudyList,
    loadKnowledgeList,
    loadReportList,
    audit,
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    handleLogin,
    handleLogout,
    reset: async () => {
      localStorage.removeItem('arcolab_session');
      setState((prev) => ({
        ...prev,
        users: [],
        projects: [],
        studies: [],
        documents: [],
        knowledge: [],
        feasibilities: [],
        risks: [],
        tasks: [],
        milestones: [],
        audit: [],
        reports: [],
      }));
    },
    upsert: (collection, item) => {
      setState((prev) => {
        const arr = prev[collection] || [];
        const idx = arr.findIndex((x) => x.id === item.id);
        const next = idx >= 0
          ? [...arr.slice(0, idx), { ...arr[idx], ...item }, ...arr.slice(idx + 1)]
          : [item, ...arr];
        return { ...prev, [collection]: next };
      });
    },
    remove: (collection, id) => {
      setState((prev) => ({
        ...prev,
        [collection]: (prev[collection] || []).filter((x) => x.id !== id)
      }));
    },
    softDelete: (collection, id, reason) => {
      setState((prev) => ({
        ...prev,
        [collection]: prev[collection].map((x) =>
          x.id === id
            ? { ...x, deleted: true, deletedAt: nowISO(), deletedBy: prev.currentUserId, deleteReason: reason }
            : x
        )
      }));
    },
    restore: (collection, id) => {
      setState((prev) => ({
        ...prev,
        [collection]: prev[collection].map((x) =>
          x.id === id
            ? { ...x, deleted: false, deletedAt: null, deleteReason: null, restoredAt: nowISO(), restoredBy: prev.currentUserId }
            : x
        )
      }));
    },
    setCurrentUser: (id) => setState((prev) => ({ ...prev, currentUserId: id }))
  }), [
    state,
    currentUser,
    currentUserData,
    currentUserLoader,
    getCurrentUserDetails,
    loadUserList,
    loadStudyList,
    loadKnowledgeList,
    loadReportList,
    audit,
    handleLogin,
    handleLogout,
  ]);

  return <DataContext.Provider value={api}>{children}</DataContext.Provider>;
}